<script setup>
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { Page } from '@dashhudson/dashing-ui/page';
import { useSecondaryNavigationPanel } from '@dashhudson/dashing-ui/secondary-navigation-panel';
import { useSidePanel } from '@dashhudson/dashing-ui/side-panel';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import DrawerProvider from '@/components/dashingUI/DrawerProvider.vue';
import SidePanelProvider from '@/components/dashingUI/SidePanelProvider.vue';
import PrimaryNavigation from '@/components/layout/navigation/PrimaryNavigation.vue';
import { useTopBannerOffsetManager } from '@/composables/layout/useTopBannerOffsetManager';
import { useCropperStore } from '@/stores/cropper';
import { useFlagStore } from '@/stores/flag';

const route = useRoute();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  showNav: {
    type: Boolean,
    default: true,
  },
});

const flagStore = useFlagStore();
const cropperStore = useCropperStore();

const { visible: sidePanelVisible } = useSidePanel();
const { visible } = useDrawer();
const { hidden, expanded } = useSecondaryNavigationPanel();

const filterSidePanelTransitionClasses = ref('-z-10');
const contentAreaTransitionClasses = ref('');
const dashingLayoutWrapperClasses = ref('');

const isDrawerOpen = computed(() => !!visible.value);
const showPageOverlay = computed(() => isDrawerOpen.value);

const secondaryNavHidden = computed(() => hidden.value);
const secondaryNavExpanded = computed(() => {
  const hasAccessToSecondaryNavViaFeatureFlag =
    flagStore.ready && flagStore.flags?.[route?.meta?.showSecondaryNavFeatureFlagOverride];

  if (route?.meta?.showSecondaryNavFeatureFlagOverride)
    return expanded.value && hasAccessToSecondaryNavViaFeatureFlag;
  return expanded.value && !route?.meta?.hideSecondaryNav;
});

const useReportLayout = computed(() => route?.meta?.report);

useTopBannerOffsetManager({ enabled: computed(() => !useReportLayout.value) });

const hidePrimaryNav = computed(() => route?.meta?.hidePrimaryNav);

const secondaryNavigationVisible = computed(
  () => !secondaryNavHidden.value && !useReportLayout.value && secondaryNavExpanded.value,
);

function handleContentTransitionStart($event) {
  const { propertyName, target } = $event;
  if (propertyName === 'transform' && target.id === 'mainContentArea') {
    filterSidePanelTransitionClasses.value = '-z-10';
    contentAreaTransitionClasses.value = 'z-10 dash-bg-background-page';
  }
}

function handleContentTransitionEnd($event) {
  const { propertyName, target } = $event;
  if (propertyName === 'transform' && target.id === 'mainContentArea') {
    if (sidePanelVisible.value) {
      filterSidePanelTransitionClasses.value = '!z-20';
      contentAreaTransitionClasses.value = 'z-10 dash-bg-background-page';
      dashingLayoutWrapperClasses.value = 'before:z-[1]';
    } else {
      filterSidePanelTransitionClasses.value = '-z-10';
      contentAreaTransitionClasses.value = '';
      dashingLayoutWrapperClasses.value = '';
    }
  }
}

const primaryNavigationStyle = computed(() => {
  if (sidePanelVisible.value) {
    const width = secondaryNavigationVisible.value
      ? 'var(--combined-navigation-width)'
      : 'var(--primary-navigation-width)';
    return { transform: `translateX(calc(-1 * ${width}))` };
  }
  return {};
});

const backgroundStyle = computed(() => {
  if (sidePanelVisible.value) {
    return { '--bg-translate-x': 'calc(-1 * var(--combined-navigation-width))' };
  }
  return {};
});

const mainContentAreaStyle = computed(() => {
  if (sidePanelVisible.value) {
    return { transform: 'translateX(calc(-1 * var(--combined-navigation-width)))' };
  }
  return {};
});

const pageClasses = computed(() => {
  const classes = [contentAreaTransitionClasses.value];

  if (
    secondaryNavigationVisible.value &&
    !sidePanelVisible.value &&
    !useReportLayout.value &&
    !hidePrimaryNav.value
  ) {
    classes.push('has-secondary-nav');
  }

  return classes;
});

const pageStyle = computed(() => {
  if (useReportLayout.value || hidePrimaryNav.value) {
    return { marginLeft: '0', width: '100%' };
  }
  if (sidePanelVisible.value) {
    return {
      marginLeft: 'var(--combined-navigation-width)',
      width: 'calc(100% - var(--combined-navigation-width))',
    };
  }
  if (secondaryNavigationVisible.value) {
    return {};
  }
  return {
    marginLeft: 'var(--primary-navigation-width)',
    width: 'calc(100% - var(--primary-navigation-width))',
  };
});

const innerPadding = computed(() => {
  if (!useReportLayout.value && !secondaryNavHidden.value && !hidePrimaryNav.value) {
    return '!px-10';
  }
  return null;
});

const isPrintLayout = computed(() => route.meta?.report || route.meta?.isSalesforceEmbed);

const roundTopLeft = computed(() => {
  if (route?.meta?.hideSecondaryNav) {
    return true;
  }
  return expanded.value !== true;
});
</script>

<template>
  <div
    class="!relative"
    :class="dashingLayoutWrapperClasses"
    :style="backgroundStyle"
    data-dashing-layout-wrapper
  >
    <PrimaryNavigation
      v-if="props.showNav"
      :hide-toggle-button="sidePanelVisible"
      class="dash-transition-md"
      :style="primaryNavigationStyle"
    />
    <Page
      id="mainContentArea"
      data-main-content-area
      :show-overlay="showPageOverlay"
      :class="pageClasses"
      :style="[mainContentAreaStyle, pageStyle]"
      :print="isPrintLayout"
      :rounded="roundTopLeft"
      @transitionstart="handleContentTransitionStart"
      @transitionend="handleContentTransitionEnd"
    >
      <div :class="innerPadding">
        <slot />
      </div>
    </Page>
    <SidePanelProvider
      :class="filterSidePanelTransitionClasses"
      :disable-click-outside="cropperStore.isDragging"
    />
    <DrawerProvider
      :class="[sidePanelVisible ? 'elevation-2xl' : '']"
      :style="mainContentAreaStyle"
      @transitionstart="handleContentTransitionStart"
      @transitionend="handleContentTransitionEnd"
    />
  </div>
</template>

<style lang="postcss" scoped>
.background-transition::before {
  transition: all 0.3s cubic-bezier(0.35, 0, 0.3, 1.1);
}
</style>
<style lang="postcss">
[data-dashing-layout-wrapper]::before {
  top: var(--dash-top-banner-offset, 0) !important;
  transform: translateX(var(--bg-translate-x, 0));
}

[data-main-content-area].has-secondary-nav {
  margin-left: var(--primary-navigation-width) !important;
  width: calc(100% - var(--primary-navigation-width)) !important;
}

@media (min-width: 1280px) {
  [data-main-content-area].has-secondary-nav {
    margin-left: var(--combined-navigation-width) !important;
    width: calc(100% - var(--combined-navigation-width)) !important;
  }
}

[data-dash-page-header] {
  padding-top: calc(var(--dash-top-banner-offset, 0) - 1px) !important;
}
</style>
